import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthContext';
import { changePassword, fetchMe } from '@/features/auth/api';
import { ApiError } from '@/lib/apiClient';
import { PoweredByApexium } from '@/components/ui/PoweredByApexium';

/**
 * Hard gate for a seeded or reset account: no part of the app is reachable until
 * the provisional código handed over by the encarregado has been replaced.
 */
export function MustChangePage() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const [atual, setAtual] = useState('');
  const [novo, setNovo] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (novo.length < 4 || confirmar.length < 4 || atual.length < 4) {
      setErro(t('sheet.codigoCamposInvalidos'));
      return;
    }
    if (novo !== confirmar) {
      setErro(t('sheet.codigoNaoCoincide'));
      return;
    }
    if (novo === atual) {
      setErro(t('sheet.codigoIgual'));
      return;
    }

    setBusy(true);
    setErro(null);
    try {
      await changePassword(atual, novo);
      setUser(await fetchMe());
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const campos = [
    { label: t('sheet.codigoAtual'), value: atual, set: setAtual },
    { label: t('sheet.codigoNovo'), value: novo, set: setNovo },
    { label: t('sheet.codigoConfirmar'), value: confirmar, set: setConfirmar },
  ];

  return (
    <div className="flex min-h-dvh items-center justify-center bg-conduril px-5 py-10">
      <div className="w-full max-w-app rounded-[22px] bg-white px-5 pb-6 pt-6 shadow-2xl md:max-w-md md:px-7 md:pb-7">
        <h1 className="text-[21px] font-semibold leading-tight text-conduril-ink">
          {t('mustChange.titulo')}
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed text-conduril-muted">{t('mustChange.lead')}</p>
        <p className="num mt-3 text-[11px] text-conduril">{user?.username}</p>

        <div className="mt-5 flex flex-col gap-3">
          {campos.map((campo) => (
            <div key={campo.label}>
              <div className="label-eyebrow">{campo.label}</div>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder="••••"
                className="field num mt-2 bg-[#fbfbfd] text-[16px] font-semibold tracking-[0.3em]"
                value={campo.value}
                onChange={(e) => {
                  campo.set(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setErro(null);
                }}
              />
            </div>
          ))}
        </div>

        {erro && (
          <div className="mt-3.5 rounded-xl border border-danger-border bg-danger-bg px-3.5 py-3 text-[12px] font-medium leading-snug text-danger-text">
            {erro}
          </div>
        )}

        <button type="button" disabled={busy} onClick={() => void submit()} className="btn-primary mt-5">
          {t('sheet.guardarCodigo')}
        </button>
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-3 w-full py-1 text-[12px] font-medium text-[#5b6480]"
        >
          {t('perfil.logout')}
        </button>

        <PoweredByApexium className="mt-6" />
      </div>
    </div>
  );
}
